import { hasAbortController, hasKeepalive, joinPath } from '../util/env.js';
import { buildAuthHeaders } from './auth.js';
import type { LogPayload, ResolvedLoggerOptions } from '../types.js';

export type TransportOutcome =
  | { kind: 'ok'; status: number }
  | { kind: 'retry'; status: number }
  | { kind: 'auth-error'; status: number }
  | { kind: 'fatal'; status: number; error?: unknown };

export interface Transport {
  send(payload: LogPayload, opts?: { signal?: AbortSignal; keepalive?: boolean }): Promise<TransportOutcome>;
  url(): string;
}

export function createFetchTransport(
  options: ResolvedLoggerOptions & { fetch: typeof fetch },
): Transport {
  const url = joinPath(options.baseUrl, options.endpointPath);
  const retrySet = new Set(options.retryStatusCodes);

  return {
    url: () => url,
    async send(payload, sendOpts) {
      const body = JSON.stringify(payload);
      let headers: Record<string, string>;
      try {
        headers = await buildAuthHeaders(options);
      } catch {
        headers = {};
      }
      headers['Content-Type'] = 'application/json';

      const init: RequestInit = {
        method: 'POST',
        headers,
        body,
        credentials: options.credentials,
      };

      if ((sendOpts?.keepalive ?? true) && hasKeepalive() && body.length < 64 * 1024) {
        init.keepalive = true;
      }

      let abortCtrl: AbortController | undefined;
      if (sendOpts?.signal) {
        init.signal = sendOpts.signal;
      } else if (hasAbortController()) {
        abortCtrl = new AbortController();
        init.signal = abortCtrl.signal;
      }

      try {
        const res = await options.fetch(url, init);
        const status = res.status;
        if (status >= 200 && status < 300) return { kind: 'ok', status };
        if (status === 401) return { kind: 'auth-error', status };
        if (retrySet.has(status)) return { kind: 'retry', status };
        return { kind: 'fatal', status };
      } catch (err) {
        // Network errors are retriable.
        return { kind: 'retry', status: 0, error: err } as TransportOutcome;
      }
    },
  };
}
